import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FiCheckCircle,
  FiEdit2,
  FiPlus,
  FiSearch,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const emptyForm = {
  code: "",
  name: "",
  description: "",
  isPaid: true,
  requiresBalance: true,
  isActive: true,
};

export default function LeaveTypes() {
  const { accessToken } = useAuth();

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadLeaveTypes = useCallback(async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/leave/types", {
        accessToken,
      });

      setLeaveTypes(response || []);
    } catch (requestError) {
      setLeaveTypes([]);
      setError(
        requestError.message || "Unable to load leave types.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadLeaveTypes();
  }, [loadLeaveTypes]);

  const filteredLeaveTypes = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return leaveTypes.filter((leaveType) => {
      const matchesSearch =
        !normalizedSearch ||
        leaveType.name.toLowerCase().includes(normalizedSearch) ||
        leaveType.code.toLowerCase().includes(normalizedSearch);

      const matchesStatus =
        status === "All" ||
        (status === "Active" && leaveType.isActive) ||
        (status === "Inactive" && !leaveType.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [leaveTypes, search, status]);

  const openCreateModal = () => {
    setEditingType(null);
    setModalOpen(true);
  };

  const openEditModal = (leaveType) => {
    setEditingType(leaveType);
    setModalOpen(true);
  };

  const saveLeaveType = async (form) => {
    setSaving(true);
    setError("");

    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        isPaid: form.isPaid,
        requiresBalance: form.requiresBalance,
        ...(editingType ? { isActive: form.isActive } : {}),
      };

      await apiRequest(
        editingType
          ? `/leave/types/${editingType.id}`
          : "/leave/types",
        {
          method: editingType ? "PATCH" : "POST",
          body: payload,
          accessToken,
        },
      );

      setModalOpen(false);
      setEditingType(null);
      await loadLeaveTypes();
    } catch (requestError) {
      setError(
        requestError.message || "Unable to save leave type.",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (leaveType) => {
    const action = leaveType.isActive ? "deactivate" : "activate";

    if (!window.confirm(`Do you want to ${action} ${leaveType.name}?`)) {
      return;
    }

    setError("");

    try {
      await apiRequest(`/leave/types/${leaveType.id}`, {
        method: "PATCH",
        body: {
          isActive: !leaveType.isActive,
        },
        accessToken,
      });

      await loadLeaveTypes();
    } catch (requestError) {
      setError(
        requestError.message || "Unable to update leave type.",
      );
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Leave Types
          </h1>

          <p className="mt-2 text-gray-500">
            Configure leave categories, paid leave rules, and balance rules.
          </p>
        </div>

        <button
          onClick={openCreateModal}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-800 text-white font-medium hover:bg-primary-900 transition"
        >
          <FiPlus size={18} />
          Add Leave Type
        </button>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by leave type name or code..."
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-primary-800"
            />
          </div>

          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="px-4 py-3 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-800"
          >
            <option value="All">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="min-h-72 flex items-center justify-center">
            <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-800 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Leave Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Payment
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Balance Rule
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredLeaveTypes.map((leaveType) => (
                  <tr
                    key={leaveType.id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                  >
                    <td className="px-6 py-5">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900">
                            {leaveType.name}
                          </p>

                          <span className="rounded-md bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-800">
                            {leaveType.code}
                          </span>
                        </div>

                        <p className="mt-1 text-sm text-gray-500">
                          {leaveType.description || "No description added"}
                        </p>
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                          leaveType.isPaid
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {leaveType.isPaid ? "Paid Leave" : "Unpaid Leave"}
                      </span>
                    </td>

                    <td className="px-6 py-5">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                          leaveType.requiresBalance
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {leaveType.requiresBalance
                          ? "Requires Balance"
                          : "No Balance Required"}
                      </span>
                    </td>

                    <td className="px-6 py-5">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                          leaveType.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {leaveType.isActive ? (
                          <FiCheckCircle size={13} />
                        ) : (
                          <FiXCircle size={13} />
                        )}

                        {leaveType.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="px-6 py-5">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditModal(leaveType)}
                          className="p-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50"
                          title="Edit leave type"
                        >
                          <FiEdit2 />
                        </button>

                        <button
                          onClick={() => toggleActive(leaveType)}
                          className={`px-3 py-2 rounded-xl text-xs font-semibold ${
                            leaveType.isActive
                              ? "bg-red-50 text-red-700 hover:bg-red-100"
                              : "bg-green-50 text-green-700 hover:bg-green-100"
                          }`}
                        >
                          {leaveType.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!filteredLeaveTypes.length ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-16 text-center text-gray-500"
                    >
                      No leave types found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LeaveTypeModal
        open={modalOpen}
        initialData={editingType}
        saving={saving}
        onClose={() => {
          if (!saving) {
            setModalOpen(false);
            setEditingType(null);
          }
        }}
        onSave={saveLeaveType}
      />
    </div>
  );
}

function LeaveTypeModal({
  open,
  initialData,
  saving,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    setForm(
      initialData
        ? {
            code: initialData.code || "",
            name: initialData.name || "",
            description: initialData.description || "",
            isPaid: Boolean(initialData.isPaid),
            requiresBalance: Boolean(initialData.requiresBalance),
            isActive: Boolean(initialData.isActive),
          }
        : emptyForm,
    );
  }, [open, initialData]);

  if (!open) return null;

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const submit = (event) => {
    event.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {initialData ? "Edit Leave Type" : "Add Leave Type"}
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Define how this leave category is handled.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-10 w-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50"
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={submit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-8">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Leave Type Name *
              </label>

              <input
                required
                maxLength={100}
                value={form.name}
                onChange={(event) =>
                  updateField("name", event.target.value)
                }
                placeholder="Casual Leave"
                className="w-full h-12 rounded-xl border border-gray-200 px-4 focus:outline-none focus:ring-2 focus:ring-primary-800"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Leave Type Code *
              </label>

              <input
                required
                maxLength={50}
                value={form.code}
                onChange={(event) =>
                  updateField(
                    "code",
                    event.target.value.toUpperCase(),
                  )
                }
                placeholder="CL"
                className="w-full h-12 rounded-xl border border-gray-200 px-4 focus:outline-none focus:ring-2 focus:ring-primary-800"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Description
              </label>

              <textarea
                rows="3"
                maxLength={500}
                value={form.description}
                onChange={(event) =>
                  updateField("description", event.target.value)
                }
                placeholder="Optional description for employees and HR."
                className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-800 resize-none"
              />
            </div>

            <ToggleField
              title="Paid Leave"
              description="Leave remains paid according to payroll policy."
              checked={form.isPaid}
              onChange={(value) => updateField("isPaid", value)}
            />

            <ToggleField
              title="Requires Leave Balance"
              description="Deduct approved days from annual allocation."
              checked={form.requiresBalance}
              onChange={(value) =>
                updateField("requiresBalance", value)
              }
            />

            {initialData ? (
              <div className="md:col-span-2">
                <ToggleField
                  title="Active"
                  description="Inactive types remain in historical records but cannot be selected for new leave."
                  checked={form.isActive}
                  onChange={(value) => updateField("isActive", value)}
                />
              </div>
            ) : null}
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-8 py-5">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="h-11 px-6 rounded-xl border border-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="h-11 px-6 rounded-xl bg-primary-800 text-white font-medium hover:bg-primary-900 disabled:opacity-50"
            >
              {saving
                ? "Saving..."
                : initialData
                  ? "Save Changes"
                  : "Create Leave Type"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ToggleField({
  title,
  description,
  checked,
  onChange,
}) {
  return (
    <label className="flex items-center justify-between gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4 cursor-pointer">
      <div>
        <p className="font-medium text-gray-900">{title}</p>
        <p className="mt-1 text-xs text-gray-500">{description}</p>
      </div>

      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 accent-primary-800"
      />
    </label>
  );
}